import React, { useCallback, useEffect, useRef } from 'react';
import { Dropdown } from "react-bootstrap";
import useChatStore from './useChatStore';
import axios from "../../API/axios";
import { useTranslation } from 'react-i18next';
import { getDisplayMessageText } from "../../utils/messageReplyUtils";
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ForwardOutlinedIcon from '@mui/icons-material/ForwardOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';

const MenuItem = ({ label, icon, onClick, danger = false }) => (
  <Dropdown.Item as="button" className={`dropdownList${danger ? " chat-message-menu-item-danger" : ""}`}>
    <span
      className="dropdown-btn"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <div className="d-flex align-items-center w-100">
        {icon ? <span className="me-2">{icon}</span> : null}
        <span className={`dropdownTxt f-12${danger ? " redText" : ""}`}>{label}</span>
      </div>
    </span>
  </Dropdown.Item>
);

const DropDownBox = ({
  visible,
  setVisibility,
  record,
  onReplyMessage,
  onForwardMessage,
}) => {
  const dropdownRef = useRef();
  const sessionId = localStorage.getItem("sessionId");
  const { t } = useTranslation();
  const language = localStorage.getItem("Lang");
  const { hideMessageForMe } = useChatStore();

  const closeMenu = () => setVisibility(false);

  const canShowActions = record?.isDeleted === false && record?.isCompleteDeleted !== true;
  const hasCopyableText = Boolean(getDisplayMessageText(record?.msg)?.trim());

  const copyMessage = async () => {
    const text = getDisplayMessageText(record?.msg)?.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const ReadChat = useCallback(async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: record.chatID,
      langKey: language === "en" ? "" : language,
    };
    try {
      await axios.post(`Message/ReadChat`, responseData);
    } catch (e) {
      console.log(e);
    }
  }, [record, sessionId, language]);

  const UnReadChat = useCallback(async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: record.chatID,
      langKey: language === "en" ? "" : language,
    };
    try {
      await axios.post(`Message/UnReadChat`, responseData);
    } catch (e) {
      console.log(e);
    }
  }, [record, sessionId, language]);

  const DeleteChat = useCallback(async (deleteEveryOne) => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      chatID: record.chatID,
      forEveryOne: deleteEveryOne,
      langKey: language === "en" ? "" : language,
    };

    try {
      await axios.delete(`Message/DeleteChat`, { data: responseData }).then(() => {
        if (!deleteEveryOne) {
          localStorage.setItem("DeletedChatId", record.chatID);
          hideMessageForMe(record.chatID);
        }
      });
    } catch (e) {
      console.log(e);
    }
  }, [record, hideMessageForMe, sessionId, language]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setVisibility(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setVisibility]);

  return (
    <>
      {visible && (
        <Dropdown.Menu
          show={visible}
          ref={dropdownRef}
          className={`InteractionModal chat-message-menu ${record.isOwned ? "position-left" : "position-right"}`}
        >
          {canShowActions && typeof onReplyMessage === "function" ? (
            <MenuItem
              label={t("Notifications.Reply")}
              icon={<ReplyOutlinedIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
              onClick={() => {
                onReplyMessage(record);
                closeMenu();
              }}
            />
          ) : null}

          {canShowActions && hasCopyableText ? (
            <MenuItem
              label={t("Notifications.Copy")}
              icon={<ContentCopyOutlinedIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
              onClick={() => {
                copyMessage();
                closeMenu();
              }}
            />
          ) : null}

          {canShowActions && hasCopyableText && typeof onForwardMessage === "function" ? (
            <MenuItem
              label={t("Notifications.Forward")}
              icon={<ForwardOutlinedIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
              onClick={() => {
                onForwardMessage(record);
                closeMenu();
              }}
            />
          ) : null}

          {canShowActions && record?.isOwned === false ? (
            record?.isRead === false ? (
              <MenuItem
                label={t("Notifications.Mark as Read")}
                icon={<MarkEmailReadOutlinedIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
                onClick={() => {
                  ReadChat();
                  closeMenu();
                }}
              />
            ) : (
              <MenuItem
                label={t("Notifications.Mark as UnRead")}
                icon={<MarkEmailUnreadOutlinedIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
                onClick={() => {
                  UnReadChat();
                  closeMenu();
                }}
              />
            )
          ) : null}

          {record?.isDeleted === false ? (
            <MenuItem
              label={t("Notifications.Delete For Me")}
              icon={<DeleteOutlineSharpIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
              danger
              onClick={() => {
                DeleteChat(false);
                closeMenu();
              }}
            />
          ) : null}

          {record?.isOwned === true && record?.isCompleteDeleted === false ? (
            <MenuItem
              label={t("Notifications.Delete For Everyone")}
              icon={<DeleteOutlineSharpIcon className="material-symbols-sharp" style={{ fontSize: 18 }} />}
              danger
              onClick={() => {
                DeleteChat(true);
                closeMenu();
              }}
            />
          ) : null}
        </Dropdown.Menu>
      )}
    </>
  );
};

export default DropDownBox;
